import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SendEmailDialog } from 'components/SendEmailDialog'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  generateOrderOut,
  generateOrderOutCheckPlaceholder,
  getMortgageClauseTemplates,
  getOrderOuts,
  postOrderOuts,
  sendOrderOutEmail,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, ButtonGroup } from 'stories/components'
import { confirm, generateErrorHtml } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs } from './constants'

const buttonGroup: Record<string, string> = {
  appraisal: 'Request For Appraisal',
  mortgage: 'Request for Verification of Mortgage/Rent',
  title: 'Request to order Title',
  insurance: 'Request for Evidence of Insurance',
  flood: 'Request for Flood Certification',
  escrow: 'Request to Open Escrow',
}
export default function OrderOuts() {
  const [isLoading, setIsLoading] = useState(false)
  const [button, setButton] = useState(Object.keys(buttonGroup)[0])
  const [data, setData] = useState<Record<string, any>>({})
  const [orgData, setOrgData] = useState<Record<string, any>>({})
  const [isSendEmailOpen, setIsSendEmailOpen] = useState(false)

  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())
  const loan = useSelector((state: any) => state.loan)

  useEffect(() => {
    setIsLoading(true)
    getOrderOuts().then((res) => {
      setData(res.data)
      setOrgData(res.data)
      setIsLoading(false)
    })
    getMortgageClauseTemplates().then((res) => {
      const mortgageClauseCplOptions: Record<string, any> = {}
      const mortgageClauseInsuranceOptions: Record<string, any> = {}

      res.data.forEach((t: any) => {
        if (t.type === 'cpl') {
          mortgageClauseCplOptions[t.id] = t.title
        } else {
          mortgageClauseInsuranceOptions[t.id] = t.title
        }
      })

      const temp = cloneDeep(inputs)
      temp.mortgageClauseCpl.options = mortgageClauseCplOptions
      temp.mortgageClauseInsurance.options = mortgageClauseInsuranceOptions
      setInputs(temp)
    })
  }, [])

  const onChange = async (key: string, value: any) => {
    const temp = cloneDeep(data)
    if (!temp[button] || typeof temp[button] !== 'object') temp[button] = {}
    temp[button][key] = value
    setData(temp)
    if (inputs[key].inputType === 'radio') {
      await onBlur(key, temp[button])
    }
  }

  const onBlur = async (key: string, _value?: any) => {
    const value = data[button]?.[key]
    if (!_value && value === orgData[button]?.[key]) return
    setIsLoading(true)
    await postOrderOuts({
      key: button,
      value: _value || data[button],
    })
    setIsLoading(false)
    setOrgData(data)
  }

  const checkPlaceholders = async (titleYes: string): Promise<boolean> => {
    const loanNumber = setLoanNumber()
    const emptyKeys = await generateOrderOutCheckPlaceholder(loanNumber, buttonGroup[button])
    if (emptyKeys.length) {
      const html = generateErrorHtml('Following fields are not filled. Please check them again.', emptyKeys)
      setIsLoading(false)
      const result = await confirm(html, { titleYes, titleNo: 'Close' })
      return !!result
    }
    return true
  }

  const onGenerate = async () => {
    setIsLoading(true)
    try {
      const isContinue = await checkPlaceholders('Generate')
      if (!isContinue) return
      setIsLoading(true)
      await generateOrderOut(setLoanNumber(), buttonGroup[button], loan.byteproFileName)
    } catch (e) {}
    setIsLoading(false)
  }

  const onOpenSendEmail = async () => {
    setIsLoading(true)
    try {
      const isContinue = await checkPlaceholders('Continue')
      if (!isContinue) return
    } catch (e) {}
    setIsLoading(false)
    setIsSendEmailOpen(true)
  }

  const onSendEmail = (emailData: Record<string, string>, selectedEmails: Record<string, any>) => {
    return sendOrderOutEmail(buttonGroup[button], emailData, selectedEmails)
  }
  return (
    <div className="SSA-container relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
        <span>Order Out</span>
        {isLoading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
      </h2>
      <div className="">
        <ButtonGroup title={buttonGroup} onChange={setButton} value={button} />
        <div className="grid grid-cols-2 gap-4">
          {Object.keys(inputs).map((key) => {
            if (
              ['coverageAmount', 'policyNumber', 'insuranceEscrowed', 'insuranceType'].includes(key) &&
              button !== 'insurance'
            )
              return
            const input = inputs[key]
            input.value = data[button]?.[key] || ''
            return (
              <div className={`col-span-${input.span || 1}`}>
                <RenderInput
                  input={input}
                  Key={key}
                  onChange={(key: string, value: any) => onChange(key, value)}
                  onBlur={(key: string) => onBlur(key)}
                />
              </div>
            )
          })}
        </div>
        <div className="flex justify-center mt-6 gap-4">
          <Button onClick={onGenerate}>Generate</Button>
          <Button onClick={onOpenSendEmail} link>
            Generate and Send Email
          </Button>
        </div>
      </div>
      {isSendEmailOpen && (
        <SendEmailDialog
          title={`Send "${buttonGroup[button]}" Document`}
          emailData={{ subject: buttonGroup[button], comment: '' }}
          onSubmit={onSendEmail}
          onClose={() => setIsSendEmailOpen(false)}
          loadParties
          modalId="sendEmailOrderOut"
        />
      )}
    </div>
  )
}
